// =============================================================================
// live-logic.ts – Pure computation functions for the LIVE tab
// =============================================================================

import { getActiveTrips, getScheduleKey, unique } from "./utils.js";
import { expandProfile } from "./profile-expander";
import type {
  Trip,
  AppConfig,
  AppState,
  LineConfig,
  LineDataMap,
  Direction,
  DayType
} from "./types.js";
import { LINE_CONFIG } from "./line-config.js";

// ─── Types ──────────────────────────────────────────────────────────────────

interface CityEntry {
  prefix: string;
  label: string;
}

export interface LiveCard {
  lineId: string;
  config: LineConfig;
  direction: Direction;
  hasTrips: boolean;
  disrupted: boolean;
  validities: string[];
  nextTrips: Trip[];
  trip: Trip | null;
  fromStop: string | null;
  preferredStop: string | null;
  toStop: string | undefined;
  compactStops: string[];
  detailStops: string[];
  scheduleKey: string;
  stopCandidates: string[];
  returnOrigins?: unknown[];
  arrival?: { stopCode: string; minutes: number } | null;
}

// ─── Constants ──────────────────────────────────────────────────────────────

export const FILTER_CITY_ORDER: CityEntry[] = [
  { prefix: "BT", label: "Busto Garolfo" },
  { prefix: "VC", label: "Villa Cortese" },
  { prefix: "DG", label: "Dairago" },
  { prefix: "AC", label: "Arconate" },
  { prefix: "OC", label: "Olcella" },
  { prefix: "SG", label: "S. Giorgio su Legnano" },
  { prefix: "LG", label: "Legnano" },
  { prefix: "PB", label: "Parabiago" },
  { prefix: "BS", label: "Busto Arsizio" },
  { prefix: "CN", label: "Canegrate" },
  { prefix: "CZ", label: "Casorezzo" },
  { prefix: "OS", label: "Ossona" },
  { prefix: "AL", label: "Arluno" },
  { prefix: "RG", label: "Rogorotto" },
  { prefix: "MN", label: "Mantegazza" },
  { prefix: "PG", label: "Pregnana Milanese" },
  { prefix: "CD", label: "Cornaredo" },
  { prefix: "VH", label: "Vighignolo" },
  { prefix: "MD", label: "Milano (Molino Dorino)" },
  { prefix: "MG", label: "Magenta" },
  { prefix: "CB", label: "Corbetta" },
  { prefix: "TI", label: "S. Stefano Ticino" },
  { prefix: "IN", label: "Inveruno" },
  { prefix: "CG", label: "Cuggiono" },
  { prefix: "BC", label: "Buscate" },
  { prefix: "CT", label: "Castano Primo" },
  { prefix: "RH", label: "Rho" },
  { prefix: "NR", label: "Nerviano" },
  { prefix: "SV", label: "S. Vittore Olona" },
  { prefix: "CR", label: "Cerro Maggiore" },
  { prefix: "CL", label: "Cantalupo" },
  { prefix: "LN", label: "Lainate" },
  { prefix: "OR", label: "Origgio" },
  { prefix: "PM", label: "Pogliano Milanese" },
  { prefix: "PR", label: "Pero" },
  { prefix: "VT", label: "Vittuone" },
  { prefix: "SD", label: "Sedriano" },
  { prefix: "BA", label: "Bareggio" },
  { prefix: "VZ", label: "Vanzago" },
  { prefix: "TB", label: "Turbigo" },
  { prefix: "NS", label: "Nosate" },
  { prefix: "VN", label: "Vanzaghello" },
  { prefix: "BE", label: "Bernate Ticino" },
  { prefix: "BF", label: "Boffalora" },
  { prefix: "RB", label: "Robecchetto" },
  { prefix: "MC", label: "Marcallo" },
  { prefix: "MS", label: "Mesero" },
  { prefix: "MM", label: "Magnago" },
  { prefix: "BI", label: "Magnago (Bienate)" },
  { prefix: "ML", label: "Milano" },
  { prefix: "TM", label: "Settimo Milanese" },
  { prefix: "SI", label: "S. Ilario Milanese" },
  { prefix: "AR", label: "Arese" },
  { prefix: "BR", label: "Barbaiana/Lainate" },
  { prefix: "VP", label: "Villastanza" },
  { prefix: "VS", label: "S. Sebastiano" }
];

// ─── Pure functions ─────────────────────────────────────────────────────────

export function resolveFavoriteStop(
  lineId: string,
  dir: string,
  state: AppState,
  cfg: AppConfig
): string | null {
  // Precedenza: Utente -> Focus City -> Globale
  const userFav = state?.settings?.favoriteStops?.[lineId]?.[dir];
  if (userFav) return userFav;

  const focusCity = state?.settings?.focusCity || cfg?.defaults?.focusCity || "BT";
  const cityFav = cfg?.focusCities?.[focusCity]?.favoriteStops?.[lineId]?.[dir];
  if (cityFav) return cityFav;

  const globalFav = cfg?.favoriteStops?.[lineId]?.[dir];
  if (globalFav) return globalFav;

  return null;
}

export function getUserFavorite(
  state: AppState,
  cfg: AppConfig,
  lineId: string,
  direction: string
): string | null {
  return resolveFavoriteStop(lineId, direction, state, cfg);
}

export function getProfile(cfg: AppConfig, lineId: string): Record<string, any> {
  const profiles = (cfg as unknown as Record<string, any>).stopProfiles;
  if (profiles && profiles[lineId]) {
    if (!profiles[lineId].__expanded) {
      profiles[lineId] = expandProfile(profiles[lineId], lineId);
      profiles[lineId].__expanded = true;
    }
    return profiles[lineId];
  }

  // Generate a fallback minimum profile using LINE_CONFIG referenceStops
  const lineConfig = (LINE_CONFIG as Record<string, any>)[lineId];
  const refStops = lineConfig?.referenceStops || [];
  const firstStop = refStops[0] || "";
  const lastStop = refStops[refStops.length - 1] || "";

  const generated = expandProfile({
    outboundHomeStops: firstStop ? [firstStop] : [],
    returnHomeStops: lastStop ? [lastStop] : [],
    compactStops: {
      outbound: lastStop ? [lastStop] : [],
      return: firstStop ? [firstStop] : []
    },
    detailStops: {
      outbound: [...refStops],
      return: [...refStops].reverse()
    },
    returnInterchanges: [],
    returnConnectionOrigins: []
  });

  if (profiles) {
    profiles[lineId] = generated;
    generated.__expanded = true;
  }

  return generated;
}

export function compareCardsByDeparture(a: LiveCard, b: LiveCard): number {
  const aMin = a.trip?._depMin ?? Number.POSITIVE_INFINITY;
  const bMin = b.trip?._depMin ?? Number.POSITIVE_INFINITY;
  if (aMin !== bMin) return aMin - bMin;
  return a.lineId.localeCompare(b.lineId);
}

export function getConfiguredStops(
  state: AppState,
  cfg: AppConfig,
  lineId: string,
  kind: string,
  direction: string,
  defaults: string[]
): string[] {
  const custom = (
    state.settings?.visibleStops as Record<string, Record<string, Record<string, string[]>>>
  )?.[lineId]?.[kind]?.[direction];
  return custom?.length ? custom : defaults;
}

export function chooseArrivalAfter(
  trip: Trip,
  candidates: string[],
  afterMin: number
): { stopCode: string; minutes: number } | null {
  for (const code of candidates) {
    const minutes = trip.stops?.[code];
    if (minutes !== undefined && minutes !== null && minutes >= afterMin)
      return { stopCode: code, minutes };
  }
  return null;
}

export function getM1FrequencyLabel(arrMin: number, cfg: AppConfig): string {
  const m1 = cfg.m1Frequency;
  if (!m1) return "ogni ~5 min";
  const isPeak = (m1.peakHours || []).some(([from, to]) => arrMin >= from && arrMin <= to);
  return isPeak ? `ogni ~${m1.peakMin} min` : `ogni ~${m1.offPeakMin} min`;
}

export function buildOutboundCard(
  lineId: string,
  state: AppState,
  lineData: LineDataMap,
  lineConfig: Record<string, LineConfig>,
  cfg: AppConfig,
  currentMin: number,
  dayType: string,
  disrupted: boolean,
  stopFilter: string | null
): LiveCard {
  const config = lineConfig[lineId];
  const profile = getProfile(cfg, lineId) as Record<string, any>;
  const scheduleKey = getScheduleKey(lineId, dayType, "outbound");
  let trips: Trip[] = lineData[lineId]?.[scheduleKey] || [];

  if (stopFilter) {
    trips = trips.filter(
      (t: Trip) => t.stops?.[stopFilter] !== undefined && t.stops?.[stopFilter] !== null
    );
  }

  const preferred = stopFilter || getUserFavorite(state, cfg, lineId, "outbound");
  const fallbacks: string[] = stopFilter
    ? []
    : profile.outboundHomeStops || config.referenceStops || [];
  const nextTrips = getActiveTrips(trips, currentMin, 3, preferred as string, fallbacks);
  const trip = nextTrips[0] || null;
  const compactStops = getConfiguredStops(
    state,
    cfg,
    lineId,
    "compactStops",
    "outbound",
    profile.compactStops?.outbound || config.referenceStops || []
  );
  const detailStops = getConfiguredStops(
    state,
    cfg,
    lineId,
    "detailStops",
    "outbound",
    profile.detailStops?.outbound || compactStops
  );

  const stopCandidates = [
    ...new Set(
      [
        preferred,
        ...(profile.outboundHomeStops || []),
        ...(profile.detailStops?.outbound || []),
        ...(profile.compactStops?.outbound || []),
        ...(config.referenceStops || [])
      ].filter(Boolean) as string[]
    )
  ];

  return {
    lineId,
    config,
    direction: "outbound",
    hasTrips: nextTrips.length > 0,
    disrupted,
    validities: unique(trips.map((t: Trip) => t.validity)),
    nextTrips,
    trip,
    fromStop: trip?._depStop || preferred || fallbacks[0] || null,
    preferredStop: preferred,
    toStop: compactStops.at(-1),
    compactStops,
    detailStops,
    scheduleKey,
    stopCandidates
  };
}

export function buildReturnCard(
  lineId: string,
  state: AppState,
  lineData: LineDataMap,
  lineConfig: Record<string, LineConfig>,
  cfg: AppConfig,
  currentMin: number,
  dayType: string,
  disrupted: boolean,
  stopFilter: string | null
): LiveCard {
  const config = lineConfig[lineId];
  const profile = getProfile(cfg, lineId) as Record<string, any>;
  const scheduleKey = getScheduleKey(lineId, dayType, "return");
  let trips: Trip[] = lineData[lineId]?.[scheduleKey] || [];

  if (stopFilter) {
    trips = trips.filter(
      (t: Trip) => t.stops?.[stopFilter] !== undefined && t.stops?.[stopFilter] !== null
    );
  }

  const interchanges: string[] = profile.returnInterchanges || [];
  const preferredInterchange: string | null =
    stopFilter || state.settings?.returnInterchanges?.[lineId] || interchanges[0] || null;
  const nextTrips = getActiveTrips(
    trips,
    currentMin,
    3,
    preferredInterchange as string,
    stopFilter ? [] : interchanges
  );
  const trip = nextTrips[0] || null;
  const homeStops = [
    getUserFavorite(state, cfg, lineId, "return"),
    ...(profile.returnHomeStops || [])
  ].filter(Boolean) as string[];
  const compactStops = getConfiguredStops(
    state,
    cfg,
    lineId,
    "compactStops",
    "return",
    profile.compactStops?.return || homeStops
  );
  const detailStops = getConfiguredStops(
    state,
    cfg,
    lineId,
    "detailStops",
    "return",
    profile.detailStops?.return || [preferredInterchange, ...homeStops]
  );
  const arrival = trip ? chooseArrivalAfter(trip, homeStops, trip._depMin!) : null;

  const stopCandidates = [
    ...new Set(
      [
        preferredInterchange,
        ...(profile.returnInterchanges || []),
        ...(profile.detailStops?.return || []),
        ...(profile.compactStops?.return || [])
      ].filter(Boolean) as string[]
    )
  ];

  return {
    lineId,
    config,
    direction: "return",
    hasTrips: nextTrips.length > 0,
    disrupted,
    validities: unique(trips.map((t: Trip) => t.validity)),
    nextTrips,
    trip,
    fromStop: trip?._depStop || preferredInterchange,
    preferredStop: preferredInterchange,
    toStop: arrival?.stopCode || homeStops[0],
    compactStops,
    detailStops,
    scheduleKey,
    returnOrigins: profile.returnConnectionOrigins || [],
    arrival,
    stopCandidates
  };
}
